import { createContext, useCallback, useContext, useEffect, useRef, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import UnlockAccountPage from './pages/UnlockAccountPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChatPage from './pages/ChatPage';
import MyPage from './pages/MyPage';
import DocumentsPage from './pages/DocumentsPage';
import DocAutomationPage from './pages/DocAutomationPage';
import GeneratedFilesPage from './pages/GeneratedFilesPage';
import InquiryPage from './pages/InquiryPage';
import CenterCostPage from './pages/CenterCostPage';        // 센터 전용 일일 비용자료
import CenterCostAllPage from './pages/CenterCostAllPage';  // 센터 비용 통합조회
import CenterShipperPage from './pages/CenterShipperPage';  // 센터 고객사 명부(일별 실적 기반)
import CenterDailyPage from './pages/CenterDailyPage';      // 센터 일별 실적(입력·요약)
import CenterPerfPage from './pages/CenterPerfPage';        // 센터 실적 대시보드(일별출고 엑셀 업로드·조회)
import AbcAnalysisPage from './pages/AbcAnalysisPage';      // 월별 SKU ABC 분석
import MeetingsPage from './pages/MeetingsPage';
import AuthorizePage from './pages/AuthorizePage';
import OfficePage from './pages/OfficePage';
import CollabToolsPage from './pages/CollabToolsPage';
import CollabViewPage from './pages/CollabViewPage';
import MainPage from './pages/MainPage';
import HelpPage from './pages/HelpPage';
import BoardPage from './pages/BoardPage';
import TicketsPage from './pages/TicketsPage';
import DxBoardPage from './pages/DxBoardPage';
import ExecMeetingPage from './pages/execmeeting/ExecMeetingPage';
import SupportBoardPage from './pages/SupportBoardPage';
import ContractsPage from './pages/ContractsPage';          // 계약 관리 목록
import ContractDetailPage from './pages/ContractDetailPage'; // 계약 관리 상세
import ContractTrashPage from './pages/contracts/ContractTrashPage'; // 계약 관리 휴지통
import ContractSettingsPage from './pages/contracts/ContractSettingsPage'; // 계약 관리 설정
import PdasPage from './pages/PdasPage';                  // 농심DAS 작업 목록
import PdasJobPage from './pages/PdasJobPage';            // 농심DAS 작업 한 건(준비·스캔·마감)
import DispatchCalcPage from './pages/DispatchCalcPage';  // 배차(적재) 계산기
import CapaPage from './pages/CapaPage';                  // 네이버 CAPA 현황·이력
import InboundCapaPage from './pages/InboundCapaPage';    // 입고 CAPA 모니터링
import ClaimRegisterPage from './pages/ClaimRegisterPage';    // 클레임 등록(단건)
import ClaimBulkPage from './pages/ClaimBulkPage';            // 클레임 엑셀 일괄등록
import ClaimStandardsPage from './pages/ClaimStandardsPage';  // 클레임 보상기준 마스터
import ClaimReasonsPage from './pages/ClaimReasonsPage';      // 클레임 사유코드 관리
import WorkCustomersPage from './pages/WorkCustomersPage';    // 업무 통합 — 고객사 기준 정보 및 이력관리
import DevRequestsPage from './pages/DevRequestsPage'; // 개발요청
import DirectoryPage from './pages/DirectoryPage';
import TaskPmPage from './pages/TaskPmPage';                // 업무 프로젝트(PMS)
import AidevRoomPage from './pages/AidevRoomPage';          // AI 개발실(④-S 화면 최소판)
import { isDevEnv } from './utils/env';
import { canSeeMenu } from './utils/menuAccess';
import { applyFont, readStoredFont } from './utils/fontPrefs';
import ViewModeToggle from './components/ViewModeToggle';
import MobileScale from './components/MobileScale';
import FloatingRecorder from './components/FloatingRecorder';
import AppDialogHost from './components/AppDialog';
import PushSetupBanner from './components/PushSetupBanner';
import { EmbedActivator, EmbedHost, EmbedHostProvider } from './components/EmbedHost';
import SplitShell, { SPLIT_APPS, isEmbedSplitApp } from './components/SplitShell';
import SidePaneRouter from './components/SidePaneRouter';
import { RecordingProvider } from './contexts/RecordingContext';
import useSessionKeepAlive from './hooks/useSessionKeepAlive';
import usePushAutoSubscribe from './hooks/usePushAutoSubscribe';
import { resumeSession, shouldTryResume, SESSION_EVENT } from './api/client';

// 관리자 페이지는 관리자만 진입(admin 전용 코드 ~4,300줄). 일반 직원 초기 번들에서 제외하려고
// 정적 import 대신 React.lazy로 별도 청크 분리 → /admin 진입 시에만 다운로드(#187).
const AdminPage = lazy(() => import('./pages/AdminPage'));
const DEV_TITLE_SUFFIX = ' - DEV';

// 🔴 부팅 상태(공유) — 세 라우트 가드가 각자 localStorage 를 읽던 것을 이 한 값으로 묶는다.
//    checking = 저장된 refresh 쿠키로 resume 을 시도하는 중(라우터·RecordingProvider 미마운트),
//    authenticated = 유효한 access 토큰 보유, anonymous = 비로그인.
const BOOT_CHECKING = 'checking';
const BOOT_AUTHENTICATED = 'authenticated';
const BOOT_ANONYMOUS = 'anonymous';
const BootContext = createContext(BOOT_ANONYMOUS);
// client 모듈을 목킹한 기존 테스트에서도 죽지 않게 폴백을 둔다(값은 client.js 와 같다).
const SESSION_CHANGE_EVENT = SESSION_EVENT || 'gw:session-change';
// resume 이 응답하지 않아도 부팅이 끝나게 하는 데드라인(axios timeout 과 이중 안전장치).
const RESUME_DEADLINE_MS = 6000;
// 🔴 만료된 access 토큰의 resume 이 비확정 실패(503·429·네트워크)했을 때 다시 시도하는 간격 —
//    60초에서 배로 늘려 5분에서 멈춘다(sid 토큰에만 준다 — 아래 60초 검사).
const RESUME_RETRY_MIN_MS = 60000;
const RESUME_RETRY_MAX_MS = 5 * 60 * 1000;

// lazy 청크 로딩 동안 잠깐 보이는 폴백(전체화면 중앙 스피너).
function RouteFallback() {
  return (
    <div role="status" aria-live="polite" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 'var(--gw-app-h, 100dvh)', color: '#6b7280', gap: '10px', padding: '24px', boxSizing: 'border-box' }}>
      <span aria-hidden="true" style={{ width: '24px', height: '24px', border: '3px solid #e5e7eb', borderTopColor: '#111827', borderRadius: '50%', display: 'inline-block', animation: 'gw-route-spin 0.8s linear infinite' }} />
      <span style={{ fontSize: '13px', fontWeight: 600 }}>화면을 불러오는 중입니다</span>
      <style>{`@keyframes gw-route-spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function getTokenExp(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

// 🔴 sid 클레임이 있는 토큰 = 모바일 지속 세션(refresh 쿠키로 되살아날 수 있다). sid 가 없는
//    PC·옛 토큰은 쿠키로 살아날 길이 없으니 비확정 실패(503·429)에도 유예를 주지 않는다
//    (유예를 주면 만료된 토큰을 쥔 채 인증 화면에 무기한 머문다 — client.js 인터셉터와 같은 판단).
function hasSidClaim(token) {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return false;
    const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return claims.sid !== undefined && claims.sid !== null;
  } catch (e) {
    return false;
  }
}

function clearSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
}

// 저장된 access 토큰이 아직 살아 있는가(만료 포함). 부팅 상태 계산의 단일 기준.
function hasLiveToken() {
  const token = localStorage.getItem('token');
  if (!token) return false;
  const exp = getTokenExp(token);
  return !(exp && Date.now() >= exp);
}

// client 모듈이 목킹된 기존 테스트에서도 죽지 않게 함수 존재를 확인한다.
function canTryResume() {
  return typeof shouldTryResume === 'function' && shouldTryResume();
}

function initialBootStatus() {
  if (hasLiveToken()) return BOOT_AUTHENTICATED;
  return canTryResume() ? BOOT_CHECKING : BOOT_ANONYMOUS;
}

// refresh 쿠키로 access 토큰을 1회 재발급한다. 🔴 실패는 '로그아웃'이 아니다 —
// 401(저장된 세션 없음)·429·5xx·네트워크 오류 어느 쪽이든 여기서 토큰을 지우지 않는다.
// 🔴 토큰 저장은 client.js 의 resumeSession() 한 곳에서만 한다(탭 안 단일 비행 + 저장 직전
//    로그아웃 재확인 + notifySessionChange) — 여기서 중복 저장하면 그 보호를 우회한다.
async function attemptResume() {
  if (typeof resumeSession !== 'function') return false;
  let timer = null;
  try {
    const out = await Promise.race([
      resumeSession(),
      // 🔴 데드라인이 없으면 응답 없는 회선에서 부팅이 checking 에 영원히 갇혀 사용자가
      //    로그인 화면에도 못 간다(라우터가 아직 없어 화면 안에 탈출구가 없다).
      new Promise((resolve) => { timer = setTimeout(() => resolve(null), RESUME_DEADLINE_MS); }),
    ]);
    // 🔴 데드라인을 넘겨 늦게 도착한 성공도 resumeSession 이 토큰을 저장하고
    //    notifySessionChange() 를 쏘므로, syncBoot 가 authenticated 로 다시 맞춘다.
    return !!(out && out.ok);
  } catch (e) {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function readCachedUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch (e) { return {}; }
}

// 🔴 세 가드는 각자 localStorage 의 토큰을 읽지 않는다 — App 이 들고 있는 공유 부팅 상태
//    하나만 본다. 인터셉터가 아니라 **동기 렌더하는 이 가드**가 자동 로그인의 첫 관문이라,
//    부팅 resume 이 끝나기 전에 여기서 /login 으로 튕기면 resume 자체가 무의미해진다.
function PrivateRoute({ children }) {
  const boot = useContext(BootContext);
  if (boot !== BOOT_AUTHENTICATED) return <Navigate to="/login" replace />;
  return children;
}

// 메뉴 접근 권한 라우트 가드 — 권한 없는 메뉴는 URL 직접 접근도 /home으로 차단(접근 권한 OFF 시).
// (home/help는 안전한 기본 페이지라 가드하지 않음 = 리다이렉트 루프 방지)
function MenuRoute({ menuKey, children }) {
  const boot = useContext(BootContext);
  if (boot !== BOOT_AUTHENTICATED) return <Navigate to="/login" replace />;
  if (!canSeeMenu(readCachedUser(), menuKey)) return <Navigate to="/home" replace />;
  return children;
}

// 센터별 화면(/center/:token) — 메뉴 키가 센터마다 다르다(center:<token> · token 은 라우터가 decode 한 값 그대로).
function CenterSiteRoute({ children }) {
  const { token } = useParams();
  return <MenuRoute menuKey={'center:' + token}>{children}</MenuRoute>;
}

function AdminRoute({ children }) {
  const boot = useContext(BootContext);
  if (boot !== BOOT_AUTHENTICATED) return <Navigate to="/login" replace />;
  const user = readCachedUser();
  // 🔴 운영 관리자(ops)도 들여보낸다. 사이드바는 ops 에게 '관리자'를 보여주고
  //    AdminPage 는 `OPS_TABS` 로 볼 수 있는 탭만 남기도록 이미 짜여 있는데,
  //    여기서 막아 **메뉴는 보이는데 누르면 튕기는 죽은 메뉴**가 돼 있었다.
  //    탭 게이트(AdminPage)와 각 API 의 권한 검사(get_current_admin)가 실제 경계다.
  if (user.role !== 'admin' && user.role !== 'ops') return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  // 세션 4시간 + 활동 시 연장(마스터 2026-08-20). 조작이 없으면 연장하지 않는다.
  useSessionKeepAlive();

  // 푸시 구독 생존 — 앱 어디에 있든 한 번 확인한다(권한 granted + 알림 사용 의사일 때만 조용히 재구독).
  usePushAutoSubscribe();

  // 🔴 공유 부팅 상태 — 라우트 가드 3곳이 이 값 하나를 본다(각자 localStorage 를 읽지 않는다).
  const [boot, setBoot] = useState(initialBootStatus);

  // 만료 토큰을 조용히 되살리는 중인가 / 비확정 실패 뒤 다음 시도 시각·간격.
  // 🔴 ref 로 둔다 — 60초 콜백이 겹쳐 resume 을 두 번 부르면 회전형 refresh 쿠키가 재사용
  //    탐지에 걸려 세션 family 가 통째로 폐기된다.
  const resumingRef = useRef(false);
  const resumeRetryAtRef = useRef(0);
  const resumeRetryMsRef = useRef(0);

  // 토큰 상태 → 부팅 상태 재계산. 로그인 성공(같은 탭)·로그아웃·탭 간 로그아웃이 모두 여기로
  // 들어와 SPA 재로드 없이 authenticated ↔ anonymous 로 전이한다.
  const syncBoot = useCallback(() => {
    setBoot((prev) => {
      if (hasLiveToken()) return BOOT_AUTHENTICATED;
      // 부팅 resume 이 도는 중이면 그 결과를 기다린다(여기서 내리면 가드가 먼저 튕긴다).
      if (prev === BOOT_CHECKING) return prev;
      return BOOT_ANONYMOUS;
    });
  }, []);

  useEffect(() => {
    window.addEventListener(SESSION_CHANGE_EVENT, syncBoot);
    window.addEventListener('storage', syncBoot);
    return () => {
      window.removeEventListener(SESSION_CHANGE_EVENT, syncBoot);
      window.removeEventListener('storage', syncBoot);
    };
  }, [syncBoot]);

  // 부팅(또는 세션 만료) 시 resume 1회. 성공하면 그대로 authenticated 로 들어간다.
  useEffect(() => {
    if (boot !== BOOT_CHECKING) return undefined;
    let alive = true;
    attemptResume().then((ok) => {
      if (!alive) return;
      setBoot(ok && hasLiveToken() ? BOOT_AUTHENTICATED : BOOT_ANONYMOUS);
    });
    return () => { alive = false; };
  }, [boot]);

  // 개인 폰트 설정 적용. 로컬 캐시를 먼저 써서 서버 응답을 기다리는 동안의 깜빡임을 막는다.
  // 서버 정본(users.ui_font)은 로그인 응답·/auth/me 에서 내려와 storeFont 로 캐시를 갱신한다.
  useEffect(() => {
    applyFont(readStoredFont());
  }, []);

  useEffect(() => {
    if (!isDevEnv()) return undefined;

    const appendDevTitleSuffix = () => {
      if (!document.title.endsWith(DEV_TITLE_SUFFIX)) {
        document.title = `${document.title}${DEV_TITLE_SUFFIX}`;
      }
    };

    appendDevTitleSuffix();

    const titleElement = document.querySelector('title');
    if (!titleElement || typeof MutationObserver === 'undefined') return undefined;

    const observer = new MutationObserver(appendDevTitleSuffix);
    observer.observe(titleElement, { childList: true, characterData: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  // 🔴 만료 토큰을 발견하면 **resume 을 먼저** 시도한다 — clearSession()·BOOT_CHECKING 을
  //    먼저 하면 checking 이 Routes·RecordingProvider 를 내려서(아래 주석) 보던 화면과
  //    입력 중인 글이 사라진다. 조용히 되살아나면 화면 전이가 아예 없다.
  useEffect(() => {
    const interval = setInterval(() => {
      if (hasLiveToken()) {                 // 되살아났으면 백오프를 되돌린다
        resumeRetryAtRef.current = 0;
        resumeRetryMsRef.current = 0;
        return;
      }
      const token = localStorage.getItem('token');
      if (!token) { syncBoot(); return; }
      if (resumingRef.current) return;      // 진행 중이면 겹쳐 부르지 않는다
      if (resumeRetryAtRef.current && Date.now() < resumeRetryAtRef.current) return;
      if (typeof resumeSession !== 'function') {
        clearSession();
        setBoot(BOOT_ANONYMOUS);
        return;
      }
      // sid 는 **만료된 그 토큰**에서 읽는다 — 아래 콜백 시점엔 토큰이 바뀌어 있을 수 있다.
      const hadSid = hasSidClaim(token);
      resumingRef.current = true;
      resumeSession().then((out) => {
        if (out && out.ok) {
          resumeRetryAtRef.current = 0;
          resumeRetryMsRef.current = 0;
          syncBoot();                       // authenticated 로 맞춘다(화면 전이 없음)
          return;
        }
        // sid 없는 토큰(PC·옛 토큰)이거나 확정 거절(401·403) → 지금까지와 같은 끝 상태로.
        if (!hadSid || (out && out.definitive)) {
          clearSession();
          setBoot(BOOT_ANONYMOUS);
          return;
        }
        // sid 토큰 + 비확정 실패(503·429·네트워크) — 토큰을 남기고 기다린다
        // (60초 → 2분 → 4분 → 5분 상한. 되살아나면 위에서 초기화한다).
        const prev = resumeRetryMsRef.current;
        const next = prev ? Math.min(prev * 2, RESUME_RETRY_MAX_MS) : RESUME_RETRY_MIN_MS;
        resumeRetryMsRef.current = next;
        resumeRetryAtRef.current = Date.now() + next;
      }).catch(() => {
        // resumeSession 은 결과 객체로 돌려주게 만들어져 있어 여기 오지 않지만,
        // client 를 목킹한 테스트에서도 죽지 않게 삼킨다(토큰은 그대로 남는다).
      }).finally(() => { resumingRef.current = false; });
    }, 60000);
    return () => clearInterval(interval);
  }, [syncBoot]);

  // 라우트 표 — 주 화면과 보조 패널(SidePaneRouter · 전용 메모리 라우터)이 같은 표를 쓴다.
  // 보조 패널은 이 표에서 그 앱의 경로를 찾아 그린다(손으로 적은 앱별 분기 없음 · #1471).
  const appRoutes = (
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/unlock-account" element={<UnlockAccountPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/authorize" element={<AuthorizePage />} />
        <Route path="/home" element={<PrivateRoute><MainPage /></PrivateRoute>} />
        <Route path="/help" element={<PrivateRoute><HelpPage /></PrivateRoute>} />
        <Route path="/directory" element={<MenuRoute menuKey="directory"><DirectoryPage /></MenuRoute>} />
        <Route path="/" element={<MenuRoute menuKey="chat"><ChatPage /></MenuRoute>} />
        <Route path="/generated" element={<MenuRoute menuKey="generated"><GeneratedFilesPage /></MenuRoute>} />
        <Route path="/chat" element={<MenuRoute menuKey="chat"><ChatPage /></MenuRoute>} />
        <Route path="/my" element={<PrivateRoute><MyPage /></PrivateRoute>} />
        <Route path="/documents" element={<MenuRoute menuKey="documents"><DocumentsPage /></MenuRoute>} />
        <Route path="/doc-automation" element={<MenuRoute menuKey="docauto"><DocAutomationPage /></MenuRoute>} />
        <Route path="/meetings" element={<MenuRoute menuKey="meetings"><MeetingsPage /></MenuRoute>} />
        <Route path="/messenger" element={<MenuRoute menuKey="messenger"><EmbedActivator app="messenger" /></MenuRoute>} />
        <Route path="/drive-app" element={<MenuRoute menuKey="drive"><EmbedActivator app="drive" /></MenuRoute>} />
        <Route path="/office" element={<MenuRoute menuKey="office"><OfficePage /></MenuRoute>} />
        {/* 드라이브(Nextcloud) 앱들을 각각 별도 메뉴로 — 같은 SSO(재로그인 없음). 현재 관리자 전용. */}
        {/* ssoUrl에 ?redirectUrl=<appRoot> — 로그인 직후 NC SSO가 기본앱(Files)으로 가던 문제(첫 메일 클릭→드라이브 노출)
            수정. user_oidc login(?redirectUrl=) 지원 확인(host strip=오픈리다이렉트 방어). drive는 기본앱=Files라 불필요. */}
        <Route path="/calendar" element={<MenuRoute menuKey="calendar"><EmbedActivator app="calendar" /></MenuRoute>} />
        {/* 옛 '메일'(NC Mail) 은 메뉴에서 뺐다(2026-08-24). 북마크·옛 링크는 새 메일로 보낸다. */}
        <Route path="/mail" element={<Navigate to="/mail2" replace />} />
        <Route path="/mail2" element={<MenuRoute menuKey="mail2"><EmbedActivator app="mail2" /></MenuRoute>} />
        <Route path="/talk" element={<MenuRoute menuKey="talk"><EmbedActivator app="talk" /></MenuRoute>} />
        <Route path="/collab-tools" element={<MenuRoute menuKey="collabtools"><CollabToolsPage /></MenuRoute>} />
        {/* AI 개발실 — 시스템 관리자 + 개인 명시 허용만(menuAccess · 서버 require_menu("apps")) */}
        <Route path="/aidev" element={<MenuRoute menuKey="apps"><AidevRoomPage /></MenuRoute>} />
        <Route path="/dx-board" element={<MenuRoute menuKey="dxboard"><DxBoardPage /></MenuRoute>} />
        <Route path="/exec-meeting" element={<MenuRoute menuKey="execmeeting"><ExecMeetingPage /></MenuRoute>} />
        <Route path="/support-board" element={<MenuRoute menuKey="supportboard"><SupportBoardPage /></MenuRoute>} />
        {/* 계약 관리 — 시스템 관리자 + 명시 허용만(menuAccess 폴백 · 서버 admin_always 와 같다) */}
        <Route path="/contracts" element={<MenuRoute menuKey="contracts"><ContractsPage /></MenuRoute>} />
        {/* 고정 경로라 /contracts/:id 보다 순위가 높다(v6) — 읽는 사람을 위해 앞에 둔다. */}
        <Route path="/contracts/trash" element={<MenuRoute menuKey="contracts"><ContractTrashPage /></MenuRoute>} />
        <Route path="/contracts/settings" element={<MenuRoute menuKey="contracts"><ContractSettingsPage /></MenuRoute>} />
        <Route path="/contracts/:id" element={<MenuRoute menuKey="contracts"><ContractDetailPage /></MenuRoute>} />
        {/* 농심DAS — 시스템 관리자 + 명시 허용만(menuAccess 폴백 · 서버 admin_always 와 같다) */}
        <Route path="/pdas" element={<MenuRoute menuKey="pdas"><PdasPage /></MenuRoute>} />
        <Route path="/pdas/:id" element={<MenuRoute menuKey="pdas"><PdasJobPage /></MenuRoute>} />
        {/* 배차 계산기 — 운영·시스템 관리자 + 명시 허용만(menuAccess 폴백 · 서버 require_menu("dispatch-calc")) */}
        <Route path="/dispatch-calc" element={<MenuRoute menuKey="dispatch-calc"><DispatchCalcPage /></MenuRoute>} />
        {/* 입고 CAPA 모니터링 — 운영·시스템 관리자 + 명시 허용만(menuAccess 폴백 · 서버 require_menu("inbound-capa")) */}
        <Route path="/inbound-capa" element={<MenuRoute menuKey="inbound-capa"><InboundCapaPage /></MenuRoute>} />
        {/* 클레임 등록(2단계 단건) — 운영·시스템 관리자 + 명시 허용만(menuAccess 폴백 · 서버 require_menu("claim-register")) */}
        <Route path="/claims/register" element={<MenuRoute menuKey="claim-register"><ClaimRegisterPage /></MenuRoute>} />
        {/* 클레임 엑셀 일괄등록(구성안 화면②) — 단건과 같은 메뉴 키(사이드바 메뉴는 늘리지 않는다 · 단건 화면 [엑셀 일괄등록] 으로 들어온다) */}
        <Route path="/claims/register/bulk" element={<MenuRoute menuKey="claim-register"><ClaimBulkPage /></MenuRoute>} />
        {/* 클레임 기준(1단계) — 운영·시스템 관리자 + 명시 허용만(menuAccess 폴백 · 서버 require_menu). 편집 단추는 GET 의 can_edit */}
        <Route path="/claims/standards" element={<MenuRoute menuKey="claim-standards"><ClaimStandardsPage /></MenuRoute>} />
        <Route path="/claims/reasons" element={<MenuRoute menuKey="claim-reasons"><ClaimReasonsPage /></MenuRoute>} />
        {/* 업무 통합 — 고객사 기준 정보(1단계) · 운영·시스템 관리자 + 명시 허용만(menuAccess 폴백 · 서버 require_menu("work-customers")).
            편집 단추는 응답의 can_edit·can_center_edit·editable_fields */}
        <Route path="/work-customers" element={<MenuRoute menuKey="work-customers"><WorkCustomersPage /></MenuRoute>} />
        {/* 네이버 CAPA — 운영·시스템 관리자 + 명시 허용만(menuAccess 폴백 · 서버 require_menu("capa")) */}
        <Route path="/capa" element={<MenuRoute menuKey="capa"><CapaPage /></MenuRoute>} />
        {/* 개발요청 — 명시 허용만(menuAccess 폴백 · 서버 require_menu("devreq")). 상세는 같은 화면의 ?id= 로 연다. */}
        <Route path="/dev-requests" element={<MenuRoute menuKey="devreq"><DevRequestsPage /></MenuRoute>} />
        {/* 업무 프로젝트(PMS) — 목록·프로젝트 안 업무 목록·설정(/tasks/:projectId/settings)도 같은 화면이다(J3a) */}
        <Route path="/tasks" element={<MenuRoute menuKey="taskpm"><TaskPmPage /></MenuRoute>} />
        <Route path="/tasks/:projectId" element={<MenuRoute menuKey="taskpm"><TaskPmPage /></MenuRoute>} />
        <Route path="/tasks/:projectId/settings" element={<MenuRoute menuKey="taskpm"><TaskPmPage /></MenuRoute>} />
        <Route path="/collab-view/:key" element={<MenuRoute menuKey="collabtools"><CollabViewPage /></MenuRoute>} />
        <Route path="/inquiries" element={<MenuRoute menuKey="inquiries"><InquiryPage /></MenuRoute>} />
        {/* 센터 메뉴 — 메뉴 권한(menu_registry) · 서버도 같은 판정(권한 없으면 API 403) */}
        <Route path="/center/:token" element={<CenterSiteRoute><CenterCostPage /></CenterSiteRoute>} />
        <Route path="/center-costs" element={<MenuRoute menuKey="center-all"><CenterCostAllPage /></MenuRoute>} />
        <Route path="/center-shippers" element={<MenuRoute menuKey="center-shippers"><CenterShipperPage /></MenuRoute>} />
        <Route path="/center-daily" element={<MenuRoute menuKey="center-daily"><CenterDailyPage /></MenuRoute>} />
        {/* 센터 실적 대시보드 — 메뉴 권한(menu_registry) · 서버도 같은 판정 */}
        <Route path="/center-performance" element={<MenuRoute menuKey="center-perf"><CenterPerfPage /></MenuRoute>} />
        <Route path="/abc-analysis" element={<PrivateRoute><AbcAnalysisPage /></PrivateRoute>} />
        {/* 사내게시판·CS티켓: 선개발(현재 관리자 전용) — MenuRoute가 비관리자 URL 접근 차단 */}
        <Route path="/board" element={<MenuRoute menuKey="board"><BoardPage /></MenuRoute>} />
        <Route path="/tickets" element={<MenuRoute menuKey="tickets"><TicketsPage /></MenuRoute>} />
        <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
      </Routes>
      </Suspense>
  );

  // 🔴 checking 인 동안에는 Routes·RecordingProvider 를 마운트하지 않는다 — 가드가 resume
  //    보다 먼저 동기 렌더돼 /login 으로 튕기는 것이 자동 로그인의 첫 관문이었다.
  // 보조 패널: 임베드 앱은 EmbedHost 가 그린다(null). page 앱은 같은 라우트 표를 보조 패널 전용 라우터로 —
  // 그 안의 이동은 주 화면 주소를 바꾸지 않고, 자기 앱 밖으로 가면 SplitShell(onEscape)이 주 화면으로 넘긴다.
  // key(paneKey)는 앱이 바뀌거나 «주/보조 전환» 때 SplitShell 이 바꾼다 → 보조 라우터를 새 initialPath 로 다시 만든다.
  const shell = (
      <RecordingProvider>
      <EmbedHostProvider>
      <SplitShell
        renderSideApp={(sideApp, pane) => (!sideApp || isEmbedSplitApp(sideApp) ? null : (
          <SidePaneRouter
            key={pane?.paneKey ?? sideApp}
            sideApp={sideApp}
            initialPath={pane?.initialPath ?? SPLIT_APPS[sideApp]?.path}
            onEscape={pane?.onEscape}
            onLocationChange={pane?.onLocationChange}
          >
            {appRoutes}
          </SidePaneRouter>
        ))}
        overlay={<EmbedHost />}
      >
      {appRoutes}
      </SplitShell>
      <FloatingRecorder />
      <AppDialogHost />
      {/* 가독성: 화면 전체 zoom 확대(임베드 MM/NC 포함). 모바일 = 모바일 배율 · 웹은 사용자 배율(기본 1.0 = 미적용) */}
      <MobileScale />
      {/* 웹/모바일 전환·글자크기 조절은 사이드바(SidebarViewControls)로 이동(플로팅이 본문 가림).
          단 로그인 등 사이드바 없는 화면에선 ViewModeToggle 플로팅 유지(비로그인 시만 노출) */}
      <ViewModeToggle />
      {/* 알림 권한·앱 설치 안내(둘 다 사용자가 한 번 눌러야 되는 것) — 조건이 안 맞으면 스스로 숨는다 */}
      <PushSetupBanner />
      </EmbedHostProvider>
      </RecordingProvider>
  );

  return (
    <BootContext.Provider value={boot}>
      <BrowserRouter>
        {boot === BOOT_CHECKING ? <RouteFallback /> : shell}
      </BrowserRouter>
    </BootContext.Provider>
  );
}
